import { ArrowLeft, Check, CircleNotch, Warning } from "@phosphor-icons/react";
import { ResumeData } from "@reactive-resume/schema";
import { Button, Card } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
import { useOptimizationSession } from "@/client/services/coach/optimization-session";
import { importResume } from "@/client/services/resume/import";
import { findResumeById } from "@/client/services/resume/resume";
import { updateResume } from "@/client/services/resume/update";
import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";

import { buildCoachPlan, type CoachPlanStage } from "./build-coach-plan";
import { normalizeKeywordList } from "./coach-live-score";
import { CoachResumeScan } from "./coach-resume-scan";
import { COACH_INTAKE_COLORS } from "./coach-theme";
import { type CoachPlanContext, restoreCoachPlan, saveCoachPlan } from "./saved-coach-plan";
import { isResumeData, isUsableResumeData, withSchemaDefaults } from "./seed-resume";

type StageId = "session" | "resume" | "analyze" | "match" | "suggestions";

type StageState = "pending" | "active" | "done";

/**
 * The checklist, with a line of copy per step.
 *
 * ⚠️ EVERY STEP IS RENDERED FROM THE FIRST FRAME, and `caption` is why that is now possible. The
 * screen used to show one spinner row reading "Fetching Job Description" while the session loaded,
 * then swap in the full list — so the student's first sight of it was a single line in an otherwise
 * empty card with no indication that four more steps were coming, and the panel appeared to grow
 * itself. Showing the whole list up front means the screen states what it is going to do and then
 * ticks items off, which is the one thing a wait screen can offer.
 *
 * `caption` is the placeholder that makes an unstarted row say something. `detail` (resolved at
 * runtime — "53 editable fields", "6 keyword gaps found") replaces it as each step completes, so a
 * pending row describes the work and a done row reports its result.
 */
const STAGES: Array<{ id: StageId; title: string; caption: string }> = [
  {
    id: "session",
    title: "Fetching Job Description",
    caption: "Reading the role and its requirements",
  },
  { id: "resume", title: "Loading your Resume", caption: "Opening your master resume" },
  {
    id: "analyze",
    title: "Analyzing Content",
    caption: "Extracting skills, experience and achievements",
  },
  {
    id: "match",
    title: "Matching with Job Requirements",
    caption: "Identifying keyword gaps and alignment",
  },
  {
    id: "suggestions",
    title: "Generating AI Suggestions",
    caption: "Crafting personalized recommendations",
  },
];

const StageRow = ({
  title,
  detail,
  state,
  isLast,
}: {
  title: string;
  detail: string;
  state: StageState;
  /** Suppresses the connector rail, which would otherwise dangle below the final step. */
  isLast: boolean;
}) => (
  <div className="relative flex items-start gap-x-4 pb-7 last:pb-0">
    {/* Connector rail between the markers. Positioned off the marker's own column so it lines up
        with the centre of the circles regardless of the row's text height, and drawn BEHIND them
        (the marker carries a solid background) so it does not show through the glyph. */}
    {!isLast && (
      <span
        aria-hidden
        className="absolute left-[13px] top-[26px] w-px"
        style={{
          bottom: "2px",
          // Faint and constant rather than "filled up to the active step". A progress rail is a
          // second progress indicator competing with the markers, and the two disagree while a step
          // is mid-flight — is the line to the active step full or empty?
          backgroundImage: `linear-gradient(to bottom, ${COACH_INTAKE_COLORS.accent}33, ${COACH_INTAKE_COLORS.accent}12)`,
        }}
      />
    )}

    <div className="relative z-10 mt-0.5 shrink-0 bg-background">
      {state === "done" ? (
        <span
          className="flex size-[26px] items-center justify-center rounded-full"
          style={{ backgroundColor: COACH_INTAKE_COLORS.accent }}
        >
          <Check size={15} weight="bold" className="text-background" />
        </span>
      ) : state === "active" ? (
        <span className="flex size-[26px] items-center justify-center">
          <CircleNotch
            size={22}
            weight="bold"
            className="animate-spin"
            style={{ color: COACH_INTAKE_COLORS.accent }}
          />
        </span>
      ) : (
        <span
          className="flex size-[26px] rounded-full border"
          style={{ borderColor: `${COACH_INTAKE_COLORS.accent}33` }}
        />
      )}
    </div>

    <div className="min-w-0 pt-0.5">
      <p
        className={cn(
          "text-sm font-medium leading-tight",
          state === "pending" ? "text-foreground/45" : "text-foreground",
        )}
      >
        {title}
      </p>
      {detail && (
        <p
          className={cn(
            "mt-1 truncate text-xs",
            state === "pending" ? "text-foreground/30" : "text-foreground/55",
          )}
        >
          {detail}
        </p>
      )}
    </div>
  </div>
);

/**
 * Coach Resume optimization intake.
 *
 * Reached from the LMS handshake when the token carries a `coachJob`. Clones the
 * student's most recent resume, asks the model for suggestions against the job
 * description the LMS pushed, then hands both to the builder.
 *
 * The checklist tracks REAL milestones, not timers. Stages 1-4 complete in well under a second; the
 * prompt call is the only slow one, so it is the stage that actually holds the spinner. All five are
 * VISIBLE from the first frame — see STAGES — but only one is ever `active`.
 *
 * ⚠️ COLOURS ARE HARDCODED HERE, from COACH_INTAKE_COLORS. This screen used `text-primary` and
 * `text-success`, which meant it inherited the app theme and rendered green ticks on a checklist
 * that is still running. See coach-theme.ts for why the fix is literal values rather than retuned
 * tokens.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachOptimizePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobUuid = searchParams.get("job");

  const startOptimization = useCoachOptimizationStore((state) => state.start);

  const [stage, setStage] = useState<StageId>("session");
  const [details, setDetails] = useState<Partial<Record<StageId, string>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState<{ resumeId: string; count: number } | null>(null);

  /**
   * The résumé the scan animation is showing.
   *
   * Lifted out of `run()`'s locals for the preview's sake: the document is resolved at stage 2 but
   * the résumé RECORD is only created near the end of the run, so there is no id to preview from for
   * most of the wait. This is the same `ResumeData` the plan is built against, which is the point —
   * the student sees the document being analysed, not a picture of one.
   */
  const [previewData, setPreviewData] = useState<ResumeData | null>(null);

  // Guards against React 18 StrictMode double-invoking the effect and paying for
  // two LLM calls (and creating two resumes) per visit.
  const runningRef = useRef(false);

  const {
    data: session,
    error: sessionError,
    isLoading: sessionLoading,
  } = useOptimizationSession(jobUuid);

  const stageIndex = STAGES.findIndex((entry) => entry.id === stage);

  const stateFor = useCallback(
    (index: number): StageState => {
      if (ready) return "done";
      if (index < stageIndex) return "done";
      // A failed run leaves the stage it died on un-ticked and un-spinning: the failure box below
      // says what went wrong, and a spinner next to it would claim work is still happening.
      if (index === stageIndex) return failure ? "pending" : "active";
      return "pending";
    },
    [stageIndex, failure, ready],
  );

  const returnToCareerCoach = useCallback(() => {
    // Opened via window.open from the student app, so the opener is the tab to go
    // back to. If it is gone (popup blocked, tab closed) fall back to the resume
    // list rather than leaving the student on a dead screen.
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    navigate("/dashboard/resumes", { replace: true });
  }, [navigate]);

  const run = useCallback(async () => {
    if (!session || !jobUuid) return;

    setFailure(null);
    setStage("session");
    setDetails({ session: "Imported successfully" });

    try {
      // ── Stage 2: clone the base resume ───────────────────────────────────────
      setStage("resume");

      // The résumé content comes FROM Career Coach. Coach Resume holds nothing of its
      // own for a student arriving here for the first time, so `seedResume` — built by
      // the LMS from the master résumé that produced the baseline score — is the
      // source, not a fallback.
      let baseData: ResumeData | undefined;
      let label = session.baseResume?.label ?? "Master resume";

      // `existingResumeId` set means: continue the résumé already being optimized for
      // this job, rather than creating another one on every visit.
      let existingResumeId: string | undefined;
      let existingResume: Awaited<ReturnType<typeof findResumeById>> | undefined;

      if (session.baseResume?.coachResumeId) {
        const base = await findResumeById({ id: session.baseResume.coachResumeId });
        if (isUsableResumeData(base.data)) {
          baseData = base.data as ResumeData;
          label = base.title || label;
          existingResumeId = base.id;
          existingResume = base;
        }
      }

      if (!baseData && isUsableResumeData(session.seedResume)) {
        baseData = session.seedResume as unknown as ResumeData;
      }

      if (!isResumeData(baseData)) {
        throw new Error(
          "We couldn't read enough from your resume to optimize it. Please make sure " +
            "your master resume in Career Coach has a summary or role descriptions, then try again.",
        );
      }

      // Applied to both paths: the LMS seed carries only content, and a résumé created
      // by an older build may predate schema additions. Either way the builder needs a
      // complete document.
      baseData = withSchemaDefaults(baseData);

      setDetails((current) => ({ ...current, resume: label }));
      // Hand the real document to the scan card. Deliberately BEFORE the slow stage, so the preview
      // is up for the several seconds the student actually spends waiting rather than appearing just
      // as the screen is about to navigate away.
      setPreviewData(baseData);

      // ── Stage 3: enumerate what the model is allowed to touch ────────────────
      // Done BEFORE creating anything. A resume with no editable prose cannot be
      // optimized, and creating it first left an unusable resume behind in the
      // student's list every time this failed.
      setStage("analyze");

      // ── Stages 3-5: the shared pipeline ──────────────────────────────────────
      //
      // Every step that used to be inlined here now lives in `buildCoachPlan`, because
      // `/dashboard/ai-optimization` runs the same optimization and was running a DIFFERENT,
      // older prompt — same builder afterwards, visibly different suggestions. One function means
      // neither entry point can get a different AI, and the guards (skill-name editable fields,
      // original-value snapshot, verification, no-op filtering, skill classification) cannot be
      // applied in one place and forgotten in the other.
      //
      // `onStage` keeps this page's staged loader narrating without it owning the steps.
      const baselineBreakdown = session.baseline.breakdown as Record<
        string,
        { score?: number } | undefined
      >;

      const planContext: CoachPlanContext = {
        jobUuid: session.job.jobUuid,
        jobDescription: session.job.jobDescription,
        baselineScore: session.baseline.score,
        baselineRoleDomainFit: Number(baselineBreakdown?.roleDomainFit?.score ?? 0),
        baselineSkillOverlap: Number(baselineBreakdown?.skillOverlap?.score ?? 0),
        fixedContribution: session.scoring.fixedContribution,
        weights: session.scoring.weights,
        projectedCeiling: session.scoring.projectedCeiling,
      };
      const restored = restoreCoachPlan(baseData.metadata.coachPlan, planContext, baseData);
      const plan =
        restored ??
        (await buildCoachPlan({
          jobUuid: session.job.jobUuid,
          jobDescription: session.job.jobDescription,
          resumeData: baseData,
          // The LMS's own sub-scores, so the model calibrates its claimed gains against real numbers
          // rather than assessing from scratch.
          baseline: {
            roleDomainFit: Number(baselineBreakdown?.roleDomainFit?.score ?? 0),
            skillOverlap: Number(baselineBreakdown?.skillOverlap?.score ?? 0),
          },
          // So the prompt can state what a sub-score point is worth in the final score, and the
          // model can put its strongest suggestions on the heavier component instead of
          // spreading claims evenly across two levers of different value.
          weights: session.scoring.weights,
          missingSkills: session.targets.missingSkills,
          presentSkills: session.targets.presentSkills,
          onStage: (stage: CoachPlanStage, detail: string) => {
            setStage(stage);
            if (detail) setDetails((current) => ({ ...current, [stage]: detail }));
          },
        }));

      const suggestions = plan.suggestions;
      const evidencedSkills = plan.skillAdditions;
      baseData = {
        ...baseData,
        metadata: { ...baseData.metadata, coachPlan: saveCoachPlan(planContext, baseData, plan) },
      };
      // Persist before entering the builder, so a new tab or a reload sees the same plan.
      if (existingResume && !restored) {
        await updateResume({ ...existingResume, data: baseData });
      }

      // Only now is the run known to be viable, so this is the first point at which
      // creating a resume is justified. Doing it earlier littered the student's
      // resume list with dead copies on every failed attempt.
      //
      // A résumé already in progress for this job is reused, so repeat visits keep
      // editing one document instead of spawning a copy each time.
      const resumeId =
        existingResumeId ??
        (
          await importResume({
            title: `${session.job.title} @ ${session.job.companyName}`.trim(),
            // PUBLIC is a requirement of the scoring pipeline, not a preference. The
            // LMS renders this résumé to a PDF by screenshotting its public page, and
            // `findOneByUsernameSlug` hard-filters `visibility: "public"` — a private
            // résumé 404s there, so no PDF is produced, the rescore fails on a 403
            // from the empty S3 key, and the student never gets an improved score.
            //
            // ⚠️ Consequence: the résumé is readable by anyone who has the
            // /{username}/{slug} URL. Accepted to keep the existing buildResumeUrl
            // render path; an HMAC-guarded print route would remove the need.
            visibility: "public",
            data: baseData,
          })
        ).id;

      const breakdown = session.baseline.breakdown as Record<
        string,
        { score?: number } | undefined
      >;

      startOptimization({
        resumeId,
        suggestions,
        originalValues: plan.originalValues,
        // Only the gains of suggestions that survived verification, so the projection cannot
        // credit points for a suggestion the student will never see.
        gains: Object.fromEntries(
          [...suggestions, ...evidencedSkills]
            .filter((entry) => plan.gains[entry.id])
            .map((entry) => [entry.id, plan.gains[entry.id]]),
        ),
        skillAdditions: evidencedSkills,
        session: {
          jobUuid: session.job.jobUuid,
          jobDescription: session.job.jobDescription,
          jobTitle: session.job.title,
          companyName: session.job.companyName,
          returnOrigin: session.returnOrigin,
          baselineScore: session.baseline.score,
          baselineBadge: session.baseline.badge,
          baselineRoleDomainFit: Number(breakdown?.roleDomainFit?.score ?? 0),
          baselineSkillOverlap: Number(breakdown?.skillOverlap?.score ?? 0),
          fixedContribution: session.scoring.fixedContribution,
          weights: session.scoring.weights,
          projectedCeiling: session.scoring.projectedCeiling,
          // Normalized at the persistence boundary: the payload is declared `string[]`
          // but `Job.requiredSkills` holds `{ skillName }` objects, and an object that
          // reaches sessionStorage survives reloads and keeps crashing the builder.
          missingSkills: normalizeKeywordList(session.targets.missingSkills),
          presentSkills: normalizeKeywordList(session.targets.presentSkills),
        },
      });

      setReady({ resumeId, count: suggestions.length });
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        "Something went wrong while preparing your resume.";

      setFailure(message);
      toast({ variant: "error", title: "Couldn't prepare your resume", description: message });
    }
  }, [session, jobUuid, startOptimization]);

  useEffect(() => {
    if (!session || runningRef.current) return;
    runningRef.current = true;
    void run();
    // `attempt` is in the deps so Try again re-runs this effect.
  }, [session, run, attempt]);

  // Auto-advance once suggestions are ready. The student can still read the
  // checklist, but nobody should have to click through a screen that is done.
  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(() => navigate(`/builder/${ready.resumeId}`, { replace: true }), 600);
    return () => clearTimeout(timer);
  }, [ready, navigate]);

  if (!jobUuid) {
    return (
      <div className="mx-auto max-w-xl space-y-4 p-8">
        <h2 className="text-2xl font-semibold tracking-tight">Missing job</h2>
        <p className="text-sm text-foreground/60">
          This page needs a job to optimize for. Please start again from Career Coach.
        </p>
      </div>
    );
  }

  const unavailable = sessionError as { response?: { data?: { message?: string } } } | null;

  // The role is picked out in the accent colour, so the heading answers "which job is this for?" at
  // a glance on a screen whose entire purpose is that one job. Split into parts rather than
  // interpolated into a string, because a highlighted span cannot survive string interpolation.
  const roleLabel = session
    ? [session.job.title, session.job.companyName].filter(Boolean).join(" @ ")
    : "";

  return (
    <div className="mx-auto flex min-h-screen max-w-5xl flex-col p-6 lg:p-10">
      <Helmet>
        <title>Preparing your tailored Resume - Coach Resume</title>
      </Helmet>

      <button
        type="button"
        onClick={returnToCareerCoach}
        className="mb-6 flex w-fit items-center gap-x-1.5 text-sm text-foreground/60 transition-colors hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Go back
      </button>

      {unavailable ? (
        <Card className="space-y-4 p-6">
          <div className="flex items-center gap-x-2">
            <Warning size={20} className="text-error" />
            <h2 className="text-lg font-medium">This session is no longer available</h2>
          </div>
          <p className="text-sm text-foreground/60">
            {unavailable.response?.data?.message ??
              "Your optimization session has expired. Please start again from Career Coach."}
          </p>
          <Button variant="secondary" onClick={returnToCareerCoach}>
            Back to Career Coach
          </Button>
        </Card>
      ) : (
        <>
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight lg:text-2xl">
              Preparing your tailored Resume
              {roleLabel && (
                <>
                  {" for "}
                  <span style={{ color: COACH_INTAKE_COLORS.accent }}>{roleLabel}</span>
                </>
              )}
            </h1>
            <p className="text-sm text-foreground/60">
              Our AI is analyzing the job description and your resume to generate relevant
              suggestions.
            </p>
          </div>

          <div className="mt-8 grid flex-1 gap-6 lg:grid-cols-2">
            <Card className="h-fit p-6">
              {/* ⚠️ NO `sessionLoading` BRANCH. There used to be one, replacing the whole list with a
                  single spinner row until the session resolved — which is the state the screen spends
                  its first moment in, so that was the student's first impression of it. The list is
                  static; `stateFor` already reports stage 1 as `active` while the session loads, which
                  is exactly what is true. */}
              <div>
                {STAGES.map((entry, index) => (
                  <StageRow
                    key={entry.id}
                    title={entry.title}
                    // Runtime detail once the step has produced one ("6 keyword gaps found"),
                    // otherwise the static description of what the step does. The session's own
                    // detail is withheld until it has actually loaded — claiming "Imported
                    // successfully" while the request is still open would be a lie the row itself
                    // contradicts.
                    detail={
                      (entry.id === "session" && sessionLoading ? "" : details[entry.id]) ||
                      entry.caption
                    }
                    state={stateFor(index)}
                    isLast={index === STAGES.length - 1}
                  />
                ))}
              </div>

              {failure && (
                <div className="mt-5 space-y-3 rounded-md border border-error/40 bg-error/5 p-3">
                  <p className="text-sm text-error">{failure}</p>
                  <div className="flex gap-x-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        runningRef.current = false;
                        setAttempt((value) => value + 1);
                      }}
                    >
                      Try again
                    </Button>
                    <Button size="sm" variant="ghost" onClick={returnToCareerCoach}>
                      Back to Career Coach
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* The résumé being analysed, under a scanning sweep. Decorative in that it carries no
                progress meaning — the checklist is the source of truth — but the DOCUMENT is real,
                which is what lets the student confirm the right résumé was picked up. */}
            <Card className="relative overflow-hidden p-6">
              <CoachResumeScan resumeData={previewData} scanning={!ready && !failure} />
            </Card>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5">
            <p className="text-xs text-foreground/50">
              This may take a few seconds. Please don&apos;t close or refresh this page.
            </p>

            <div className="flex gap-x-2">
              <Button variant="ghost" onClick={returnToCareerCoach}>
                Cancel
              </Button>
              <Button
                disabled={!ready}
                onClick={() => ready && navigate(`/builder/${ready.resumeId}`, { replace: true })}
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
